import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, CheckCircle2, Clock, Gauge, Info, PackageCheck, ShoppingCart, Timer, Truck } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Card, cx, Skeleton, Tip } from "@/components/ui";
import { api, type Performance } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale, t } from "@/lib/i18n";

type Days = 7 | 30 | 90;
const pct = (x: number | null | undefined) => (x == null ? "—" : `${(x * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} %`);
const hours = (h: number | null | undefined) => (h == null ? "—" : h < 48 ? `${h.toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} h` : `${(h / 24).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} Tage`);

/** Leistungskennzahlen: wie schnell und pünktlich versendet Logiful? */
export function PerformancePage() {
  const { tenantId } = useTenant();
  const [params, setParams] = useSearchParams();
  const days = ([7, 30, 90] as const).find((d) => d === Number(params.get("days"))) ?? 30;
  const q = useQuery({ queryKey: ["performance", tenantId, days], queryFn: () => api.performance(tenantId, days), placeholderData: keepPreviousData, refetchInterval: 5 * 60_000 });
  const d = q.data;
  const setDays = (x: Days) => setParams(x === 30 ? {} : { days: String(x) }, { replace: true });

  return (
    <div className="pb-16">
      <PageHeader
        title={t("Leistung")}
        description={t("Wie schnell und pünktlich eure Aufträge das Lager verlassen – transparent und tagesaktuell.")}
        actions={
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t("Zeitraum")}>
            {([7, 30, 90] as const).map((x) => (
              <button key={x} role="radio" aria-checked={days === x} onClick={() => setDays(x)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium", days === x ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{x} {t("Tage")}</button>
            ))}
          </div>
        }
      />
      <div className="px-4 md:px-8 space-y-4">
        {!d ? (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
              <Kpi icon={<CheckCircle2 className="size-4" />} label={t("Pünktlich versendet")} value={pct(d.totals.onTimeRate)} delta={d.totals.onTimeRate != null && d.totals.prev.onTimeRate != null ? d.totals.onTimeRate - d.totals.prev.onTimeRate : null} unit="Pkt." tip={t("Ab Übergabe an Logiful: bis zum Annahmeschluss der Versandart übergeben → am selben Werktag versendet, sonst am nächsten Werktag. Wartezeiten beim Kunden (Adresse, fehlende Ware) zählen nicht.")} sub={t("{v} von {v2}", { v: fmtNumber(d.totals.onTime), v2: fmtNumber(d.totals.measured) })} tone={d.totals.onTimeRate == null ? undefined : d.totals.onTimeRate >= 0.95 ? "success" : d.totals.onTimeRate >= 0.85 ? "warning" : "danger"} />
              <Kpi icon={<PackageCheck className="size-4" />} label={t("Versendet")} value={fmtNumber(d.totals.shipped)} delta={d.totals.prev.shipped ? (d.totals.shipped - d.totals.prev.shipped) / d.totals.prev.shipped : null} unit="%" sub={t("Vorzeitraum {v}", { v: fmtNumber(d.totals.prev.shipped) })} />
              <Kpi icon={<Timer className="size-4" />} label={t("Übergabe → Versand")} value={hours(d.totals.medianHandoverH)} tip={t("Median: die Hälfte der Aufträge ist schneller raus. 90 % innerhalb des zweiten Werts.")} sub={t("90 % in {v}", { v: hours(d.totals.p90HandoverH) })} />
              <Kpi icon={<ShoppingCart className="size-4" />} label={t("Bestellung → Versand")} value={hours(d.totals.medianOrderH)} tip={t("Median aus Sicht eurer Endkunden – inkl. Stornofenster und Wartezeiten beim Kunden.")} sub={t("{v} Aufträge eingegangen", { v: fmtNumber(d.totals.received) })} />
            </div>

            <Card className="p-5">
              <div className="flex flex-wrap items-baseline gap-3 mb-3">
                <h2 className="font-semibold">{t("Eingang & Versand je Tag")}</h2>
                <span className="inline-flex items-center gap-3 text-[12px] text-fg-muted">
                  <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-fg-subtle/50" />{t("eingegangen")}</span>
                  <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-ink" />{t("versendet")}</span>
                  <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-danger" />{t("davon verspätet")}</span>
                </span>
              </div>
              <DailyChart daily={d.daily} />
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <Card className="overflow-hidden">
                <div className="px-5 pt-4 pb-3"><h2 className="font-semibold flex items-center gap-2"><Truck className="size-4 text-fg-subtle" /> {t("Versanddienstleister")}</h2></div>
                {d.carriers.length ? (
                  <table className="w-full text-[13px]">
                    <thead className="text-[12px] text-fg-subtle border-y border-border bg-surface-2/50">
                      <tr><th className="h-8 pl-5 pr-3 text-left font-medium">{t("Dienstleister")}</th><th className="h-8 px-3 text-right font-medium">{t("Sendungen")}</th><th className="h-8 px-3 text-right font-medium">{t("Anteil")}</th><th className="h-8 px-3 text-right font-medium">{t("Pünktlich")}</th><th className="h-8 pl-3 pr-5 text-right font-medium">{t("Übergabe → Versand")}</th></tr>
                    </thead>
                    <tbody>
                      {d.carriers.map((c) => (
                        <tr key={c.carrier} className="border-b border-border last:border-0">
                          <td className="pl-5 pr-3 py-2 font-medium">{c.carrier}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmtNumber(c.shipped)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-fg-muted">{pct(d.totals.shipped ? c.shipped / d.totals.shipped : null)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(c.onTimeRate)}</td>
                          <td className="pl-3 pr-5 py-2 text-right tabular-nums text-fg-muted">{hours(c.medianHandoverH)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p className="px-5 pb-5 text-[13px] text-fg-muted">{t("Im Zeitraum noch nichts versendet.")}</p>}
                <p className="px-5 py-3 border-t border-border text-[12px] text-fg-subtle flex items-start gap-1.5"><Info className="size-3.5 mt-0.5 shrink-0" /> <span>{t("Laufzeiten bis zum Empfänger und Zustellprobleme:")} <Link to="/tracking" className="underline underline-offset-2 hover:text-fg">{t("Track & Trace")}</Link></span></p>
              </Card>

              <Card className="p-5">
                <h2 className="font-semibold flex items-center gap-2"><Clock className="size-4 text-fg-subtle" /> {t("Gerade bei Logiful in Arbeit")}</h2>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-surface-2/60 px-4 py-3"><p className="text-[12px] text-fg-muted">{t("offen im Lager")}</p><p className="text-[22px] font-semibold tabular-nums">{fmtNumber(d.backlog.open)}</p>{d.backlog.oldestHandedOverAt && <p className="text-[11.5px] text-fg-subtle">{t("ältester übergeben")} {fmtRelative(d.backlog.oldestHandedOverAt)}</p>}</div>
                  <div className={cx("rounded-xl px-4 py-3", d.backlog.overdue ? "bg-danger-soft/70" : "bg-success-soft/60")}><p className="text-[12px] text-fg-muted">{t("über der Frist")}</p><p className={cx("text-[22px] font-semibold tabular-nums", d.backlog.overdue ? "text-danger" : "text-success")}>{fmtNumber(d.backlog.overdue)}</p><p className="text-[11.5px] text-fg-subtle">{d.backlog.overdue ? t("Logiful ist informiert") : t("alles im Plan")}</p></div>
                </div>
                {d.backlog.overdueOrders.length > 0 && (
                  <ul className="mt-3 divide-y divide-border border-t border-border">
                    {d.backlog.overdueOrders.map((o) => (
                      <li key={o.id}>
                        <Link to={`/orders?open=${o.id}`} className="flex items-center gap-3 py-2 text-[13px] hover:bg-surface-2/50 -mx-2 px-2 rounded-md">
                          <span className="font-mono font-medium">{o.orderNumber}</span>
                          <span className="text-fg-muted">{t("übergeben")} {fmtRelative(o.handedOverAt)}</span>
                          <span className="ml-auto text-danger text-[12px]">{t("Frist")} {fmtRelative(o.deadline)}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, tip, delta, unit, tone }: { icon: React.ReactNode; label: string; value: string; sub?: string; tip?: string; delta?: number | null; unit?: string; tone?: "success" | "warning" | "danger" }) {
  const head = (
    <span className="flex items-center gap-2 text-fg-muted text-[12.5px]"><span className="text-fg-subtle">{icon}</span>{t(label)}{tip && <Info className="size-3 text-fg-subtle" />}</span>
  );
  return (
    <Card className="p-4">
      {tip ? <Tip content={tip}>{head}</Tip> : head}
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={cx("text-[26px] font-semibold tracking-tight tabular-nums whitespace-nowrap", tone === "success" && "text-success", tone === "warning" && "text-warning", tone === "danger" && "text-danger")}>{value}</span>
        {delta != null && Math.abs(delta) >= 0.001 && (
          <Badge tone={delta > 0 ? "success" : "warning"} icon={delta < 0 ? <ArrowDownRight className="size-3" /> : <ArrowUpRight className="size-3" />}>
            {(Math.abs(delta) * 100).toLocaleString(intlLocale(), { maximumFractionDigits: 1 })} {unit}
          </Badge>
        )}
      </div>
      {sub && <p className="text-[12px] text-fg-subtle">{t(sub)}</p>}
    </Card>
  );
}

function DailyChart({ daily }: { daily: Performance["daily"] }) {
  const max = Math.max(1, ...daily.flatMap((d) => [d.received, d.shipped]));
  const dense = daily.length > 40;
  return (
    <div>
      <div className="h-40 flex items-end gap-[3px]">
        {daily.map((d) => {
          const late = d.measured - d.onTime;
          const date = new Date(d.day);
          return (
            <Tip key={d.day} content={t("{v}: {received} eingegangen · {shipped} versendet{v2}", { v: date.toLocaleDateString(intlLocale(), { weekday: "short", day: "2-digit", month: "2-digit" }), received: d.received, shipped: d.shipped, v2: d.measured ? t(" · {v} pünktlich", { v: pct(d.onTime / d.measured) }) : "" })}>
              <div className="flex-1 min-w-0 h-full flex items-end gap-px">
                {!dense && <div className="flex-1 rounded-t-[2px] bg-fg-subtle/40" style={{ height: `${(d.received / max) * 100}%` }} />}
                <div className="flex-1 rounded-t-[2px] bg-ink flex flex-col justify-start overflow-hidden" style={{ height: `${Math.max(d.shipped ? 2 : 0, (d.shipped / max) * 100)}%` }}>
                  {late > 0 && <div className="bg-danger" style={{ height: `${(late / Math.max(1, d.shipped)) * 100}%` }} />}
                </div>
              </div>
            </Tip>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[11px] text-fg-subtle">
        <span>{daily[0] ? new Date(daily[0].day).toLocaleDateString(intlLocale(), { day: "2-digit", month: "2-digit" }) : ""}</span>
        <span className="inline-flex items-center gap-1"><Gauge className="size-3" /> {t("heute")}</span>
      </div>
    </div>
  );
}
